import { Box, Stack } from '@mantine/core'
import { memo, useCallback, useRef } from 'react'
import { useBoggleRunKeyboard } from '~/boggle/ui/run/useBoggleRunKeyboard'
import { BOGGLE_OVERTIME_START_VALUE } from '~/boggle/content/timer'
import type { BoggleGameState } from '~/boggle/engine/standard/types'
import { Canvas } from '~/boggle/ui/run/Canvas'
import { SelectionWordStrip, SubmitPlayRow } from '~/boggle/ui/run/SelectionPanel'
import { RunPauseOverlay } from '~/boggle/ui/run/RunPauseOverlay'
import {
  FloatingResetSkillButton,
  SkillControls,
  useBoggleSkillControlModel,
} from '~/boggle/ui/run/SkillControls'
import type { BoggleStore } from '~/boggle/ui/state/Store'
import { useBoggleIsInOvertime } from '~/boggle/ui/state/Store'
import { useBoggleImperativeTimePaint } from '~/boggle/ui/run/useBoggleImperativeTimePaint'
import classes from './BoardFrame.module.css'

function mergeClassNames(...classNames: Array<false | null | string | undefined>) {
  return classNames.filter(Boolean).join(' ')
}

const MAX_CANVAS_SIZE = 600

const BoardCanvasColumn = memo(function BoardCanvasColumn({
  isPaused,
  state,
  store,
}: {
  isPaused: boolean
  state: BoggleGameState
  store: BoggleStore
}) {
  return (
    <Box className={classes.stage}>
      <Box
        className={mergeClassNames(
          classes.boardFrame,
          state.phase === 'ended' && classes.boardFrameEnded,
          isPaused && classes.boardRegionHiddenForPauseMenu,
        )}
        pos="relative"
        style={{
          aspectRatio: `${state.cols} / ${state.rows}`,
          width: `min(100cqw, ${(state.cols / state.rows) * 100}cqh, ${MAX_CANVAS_SIZE}px)`,
        }}
      >
        <Box className={classes.canvasLayer}>
          <Canvas store={store} />
          <OvertimeBar store={store} />
        </Box>
      </Box>
    </Box>
  )
})

export function getBoggleOvertimeFillPercent(overtimeRemaining: null | number) {
  if (overtimeRemaining === null) {
    return 0
  }

  return Math.max(
    0,
    Math.min(100, (overtimeRemaining / BOGGLE_OVERTIME_START_VALUE) * 100),
  )
}

function OvertimeBar({ store }: { store: BoggleStore }) {
  const isInOvertime = useBoggleIsInOvertime(store)
  const fillRef = useRef<HTMLDivElement>(null)

  const paint = useCallback(() => {
    const remaining = store.getTimeState().overtimeRemaining
    const fill = fillRef.current
    if (!fill || remaining === null) {
      return
    }
    fill.style.width = `${getBoggleOvertimeFillPercent(remaining)}%`
  }, [store])

  useBoggleImperativeTimePaint(paint, [], store)

  if (!isInOvertime) {
    return null
  }

  return (
    <Box
      aria-label="Overtime remaining"
      bg="rgba(255, 224, 138, 0.2)"
      h={5}
      left={30}
      bdrs={5}
      pos="absolute"
      right={30}
      top={-12}
    >
      <Box ref={fillRef} bg="#f0c85a" h="100%" style={{ width: '0%' }} />
    </Box>
  )
}

export function BoardFrame({
  accentColor,
  playAccentColor,
  bufferedRunState,
  isPaused,
  onBackToChallengeFromPause,
  onEndRunFromPause,
  onGameRules,
  pauseOverlayEndRunLabel,
  onRestart,
  showEndBuffer,
  state,
  store,
}: {
  accentColor?: string
  /** Selection play control; defaults to mode color when `gameRulesDefinition` is wired on `RunSurface`. */
  playAccentColor?: string
  bufferedRunState: BoggleGameState
  isPaused: boolean
  onBackToChallengeFromPause?: () => void
  onEndRunFromPause?: () => void
  onGameRules?: () => void
  pauseOverlayEndRunLabel?: string
  onRestart: () => void
  showEndBuffer: boolean
  state: BoggleGameState
  store: BoggleStore
}) {
  const controlsState = showEndBuffer ? bufferedRunState : state
  const skillControlModel = useBoggleSkillControlModel(controlsState, store)
  const controlsSuspended = showEndBuffer || isPaused
  const showSelectionChrome = state.phase !== 'ended'

  useBoggleRunKeyboard(store, state.phase === 'playing')

  return (
    <Box
      flex={1}
      mih={0}
      pos="relative"
      style={{ display: 'flex', flexDirection: 'column', width: '100%' }}
    >
      <BoardCanvasColumn isPaused={isPaused} state={state} store={store} />
      {showSelectionChrome ? (
        <>
          <Stack
            pt={6}
            w="100%"
            align="center"
            className={mergeClassNames(
              classes.controlsDockTop,
              controlsSuspended && classes.controlsDockTopDisabled,
            )}
          >
            <Stack w="100%" align="center" maw={360} gap={12}>
              <SelectionWordStrip
                isPaused={isPaused}
                state={controlsState}
                store={store}
              />
              <SubmitPlayRow
                isPaused={isPaused}
                playAccentColor={playAccentColor}
                state={controlsState}
                store={store}
              />
            </Stack>
          </Stack>
          <Box
            className={mergeClassNames(
              classes.controlsDock,
              controlsSuspended && classes.controlsDockDisabled,
            )}
          >
            <Stack gap={20} pb={80}>
              <SkillControls
                model={skillControlModel}
                state={controlsState}
                store={store}
              />
            </Stack>
          </Box>
          <FloatingResetSkillButton
            isPaused={isPaused}
            model={skillControlModel}
            runUiSuspended={showEndBuffer}
            state={controlsState}
            store={store}
          />
        </>
      ) : null}
      {isPaused ? (
        <RunPauseOverlay
          accentColor={accentColor}
          endRunLabel={pauseOverlayEndRunLabel}
          onBackToChallenge={onBackToChallengeFromPause}
          onEndRun={onEndRunFromPause}
          onGameRules={onGameRules}
          onRestart={onRestart}
          resolvedChallenge={state.resolvedChallenge}
          store={store}
        />
      ) : null}
    </Box>
  )
}
